import {
  type AddAdmin,
  type AddDueDateOverride,
  AdminListSchema,
  AdminMeSchema,
  AdminPrivacyRequestListSchema,
  type AdminRole,
  AdminStatusSchema,
  type BreachAudience,
  type BreachNotice,
  BreachNoticeResultSchema,
  BreachPreviewSchema,
  type CreateFlag,
  DueDateOverrideListSchema,
  DueDateOverrideResponseSchema,
  FlagListSchema,
  type PostStatusUpdate,
  PrivacyRequestResponseSchema,
  PublicStatusSchema,
} from '@oneapp/shared';
import { api } from '../lib/api';

const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {});
const signedIn = (signal?: AbortSignal) => ({ auth: true, ...withSignal(signal) });
const enc = encodeURIComponent;

/**
 * The admin console (gap 2, packages/shared/src/console.ts): what an admin or the Grievance Officer sees
 * and changes, signed in with their own mobile. The riskiest calls (breach notices, flags, admins) answer
 * 401 REAUTH_NEEDED when the sign-in isn't recent: `useStepUp` asks them to confirm it's them, then tries again.
 */
export const adminApi = {
  me: (signal?: AbortSignal) => api.get('/api/v1/admin/me', AdminMeSchema, signedIn(signal)),

  // Privacy requests (X3, X4): admins and Grievance Officers.
  requests: (signal?: AbortSignal) =>
    api.get('/api/v1/admin/privacy-requests', AdminPrivacyRequestListSchema, signedIn(signal)),
  answer: (id: string, answer: string) =>
    api.post(`/api/v1/admin/privacy-requests/${enc(id)}/answer`, PrivacyRequestResponseSchema, {
      auth: true,
      body: { answer },
    }),

  // Breach notice (X5).
  breachPreview: (who: BreachAudience) =>
    api.post('/api/v1/admin/breach-notices/preview', BreachPreviewSchema, { auth: true, body: who }),
  sendBreach: (notice: BreachNotice) =>
    api.post('/api/v1/admin/breach-notices', BreachNoticeResultSchema, { auth: true, body: notice }),

  // Due dates (X6).
  dueDates: (signal?: AbortSignal) => api.get('/api/v1/admin/due-dates', DueDateOverrideListSchema, signedIn(signal)),
  addDueDate: (input: AddDueDateOverride) =>
    api.post('/api/v1/admin/due-dates', DueDateOverrideResponseSchema, { auth: true, body: input }),
  withdrawDueDate: (id: string) =>
    api.delete(`/api/v1/admin/due-dates/${enc(id)}`, DueDateOverrideResponseSchema, { auth: true }),

  // Feature flags (X7).
  flags: (signal?: AbortSignal) => api.get('/api/v1/admin/flags', FlagListSchema, signedIn(signal)),
  createFlag: (input: CreateFlag) => api.post('/api/v1/admin/flags', FlagListSchema, { auth: true, body: input }),
  setFlag: (key: string, here: boolean) =>
    api.put(`/api/v1/admin/flags/${enc(key)}`, FlagListSchema, { auth: true, body: { here } }),
  addFlagPerson: (key: string, phone: string) =>
    api.post(`/api/v1/admin/flags/${enc(key)}/people`, FlagListSchema, { auth: true, body: { phone } }),
  removeFlagPerson: (key: string, userId: string) =>
    api.delete(`/api/v1/admin/flags/${enc(key)}/people/${enc(userId)}`, FlagListSchema, { auth: true }),

  addFlagBusiness: (key: string, gstin: string) =>
    api.post(`/api/v1/admin/flags/${enc(key)}/businesses`, FlagListSchema, { auth: true, body: { gstin } }),
  removeFlagBusiness: (key: string, businessId: string) =>
    api.delete(`/api/v1/admin/flags/${enc(key)}/businesses/${enc(businessId)}`, FlagListSchema, { auth: true }),

  // Admins (X8).
  admins: (signal?: AbortSignal) => api.get('/api/v1/admin/admins', AdminListSchema, signedIn(signal)),
  addAdmin: (input: AddAdmin) => api.post('/api/v1/admin/admins', AdminListSchema, { auth: true, body: input }),
  removeAdmin: (userId: string, role: AdminRole) =>
    api.delete(`/api/v1/admin/admins/${enc(userId)}/${enc(role)}`, AdminListSchema, { auth: true }),

  // Status updates (X9).
  status: (signal?: AbortSignal) => api.get('/api/v1/admin/status', AdminStatusSchema, signedIn(signal)),
  postStatus: (input: PostStatusUpdate) =>
    api.post('/api/v1/admin/status-updates', AdminStatusSchema, { auth: true, body: input }),
};

/** The status page (L2) and the chat's line about it: no sign-in. */
export const statusApi = {
  get: (signal?: AbortSignal) => api.get('/api/v1/status', PublicStatusSchema, withSignal(signal)),
  /** The status page itself asks the server each time: an update just posted shows at once, not after the cache. */
  fresh: (signal?: AbortSignal) =>
    api.get('/api/v1/status', PublicStatusSchema, { ...withSignal(signal), cache: 'no-cache' }),
};

export const adminKeys = {
  all: ['admin'] as const,
  me: ['admin', 'me'] as const,
  requests: ['admin', 'privacy-requests'] as const,
  dueDates: ['admin', 'due-dates'] as const,
  flags: ['admin', 'flags'] as const,
  admins: ['admin', 'admins'] as const,
  status: ['admin', 'status'] as const,
  publicStatus: ['status'] as const,
};
